import React from "react"
import {
    StyledModal,
    ModalContent,
    LeftColumn,
    RightColumn,
    ProfileSection,
    RoleBadge,
    PartyBadge,
    DetailsSection,
    DetailRow,
    DetailLabel,
    DetailValue,
    WebsiteLink,
    ExternalLinkIcon,
    SocialLinksSection,
    SocialLink,
    StyledAvatar,
} from "QuorumGrassroots/widgets/ReusableComponents/CandidateDetailsModal/CandidateDetailsModal.styles"
import { CandidateDetailsModalProps } from "QuorumGrassroots/widgets/ReusableComponents/CandidateDetailsModal/CandidateDetailsModal.types"
import { XTwitterFarFAIcon } from "QuorumGrassroots/widgets/ReusableComponents/Icons"

// Party label mapping
const PARTY_LABELS: Record<string, string> = {
    D: "DEMOCRAT",
    R: "REPUBLICAN",
    I: "INDEPENDENT",
    Democrat: "DEMOCRAT",
    Republican: "REPUBLICAN",
    Independent: "INDEPENDENT",
}

// Get party label from mapping or uppercase the value
const getPartyLabel = (party?: string): string => {
    if (!party) return ""
    return PARTY_LABELS[party] || party.toUpperCase()
}

// Social media platform configuration
const SOCIAL_PLATFORMS = [
    { key: "facebook" as const, icon: "fa-facebook", label: "Facebook" },
    { key: "twitter" as const, icon: "fa-x-twitter", label: "X (Twitter)" },
    { key: "linkedin" as const, icon: "fa-linkedin", label: "LinkedIn" },
    { key: "instagram" as const, icon: "fa-instagram", label: "Instagram" },
]

export const CandidateDetailsModal: React.FC<CandidateDetailsModalProps> = ({ candidate, isOpen, onClose }) => {
    if (!candidate) {
        return null
    }

    // Build social links from candidate data
    const socialLinks = SOCIAL_PLATFORMS.filter((platform) => candidate[platform.key]).map((platform) => ({
        ...platform,
        url: candidate[platform.key]!,
    }))

    return (
        <StyledModal opened={isOpen} onClose={onClose} title={candidate.full_name} centered size="xl">
            <ModalContent>
                {/* Left Column: Photo, Role, Party, Social Links */}
                <LeftColumn>
                    <ProfileSection>
                        {/* <Avatar src={candidate.image_url} alt={candidate.full_name} size="xl" /> */}
                        <StyledAvatar src={candidate.image_url} alt={candidate.full_name} radius="md">
                            {!candidate.image_url && candidate.full_name.charAt(0)}
                        </StyledAvatar>
                        {candidate.candidate.title && <RoleBadge>{candidate.candidate.title}</RoleBadge>}
                        {candidate.party && <PartyBadge>{getPartyLabel(candidate.party)}</PartyBadge>}
                    </ProfileSection>

                    {socialLinks.length > 0 && (
                        <SocialLinksSection>
                            {socialLinks.map((link) => (
                                <SocialLink
                                    key={link.icon}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Visit ${candidate.full_name}'s ${link.label} profile`}
                                >
                                    {link.icon === "fa-x-twitter" ? (
                                        <XTwitterFarFAIcon size={18} color="#495057" />
                                    ) : (
                                        <i className={`fa ${link.icon}`} aria-hidden="true" />
                                    )}
                                </SocialLink>
                            ))}
                        </SocialLinksSection>
                    )}
                </LeftColumn>

                {/* Right Column: Contact Information */}
                <RightColumn>
                    <DetailsSection>
                        {candidate.phone && (
                            <DetailRow>
                                <DetailLabel>Phone number</DetailLabel>
                                <DetailValue>{candidate.phone}</DetailValue>
                            </DetailRow>
                        )}

                        {candidate.website && (
                            <DetailRow>
                                <DetailLabel>Website</DetailLabel>
                                <WebsiteLink href={candidate.website} target="_blank" rel="noopener noreferrer">
                                    {candidate.website}
                                    <ExternalLinkIcon className="fa fa-external-link" aria-hidden="true" />
                                </WebsiteLink>
                            </DetailRow>
                        )}

                        {candidate.address && (
                            <DetailRow>
                                <DetailLabel>Address</DetailLabel>
                                <DetailValue>{candidate.address}</DetailValue>
                            </DetailRow>
                        )}
                    </DetailsSection>
                </RightColumn>
            </ModalContent>
        </StyledModal>
    )
}
